/**
 * 员工使用分析 —— 前端采集器（usageTracker）
 *
 * ============================== 需求背景 ==============================
 * 「有效使用时长」不能等于登录时长：只有页面处于前台可见、且最近 5 分钟内有鼠标 /
 * 键盘 / 滚动 / 触摸 / 业务操作时才算。所以采集器要同时回答三个问题：
 *   1. 页面现在是否前台可见（visibilityState）；
 *   2. 用户最近有没有交互（5 分钟空闲阈值）；
 *   3. 这段时间属于哪个业务模块（当前路由）。
 *
 * ============================== 关键规则 ==============================
 * · 60 秒心跳；切后台 / 回前台 / 退出登录 / 页面隐藏立即上报，不等定时器。
 * · 同一浏览器多标签、多设备并发时，**时长不重复累计**：服务端按 (用户, 分钟) 位图去重，
 *   前端这一层再做「同一浏览器只有主标签上报」，减少无效请求，但即使选主失败也不会放大。
 * · 业务动作 / 模块打开次数按「增量 + eventId」上报：网络重试复用同一个 eventId，
 *   服务端据此幂等，不会把一次点击记成两次。
 * · 只记录动作**类型**（搜索 / 创建 / 编辑 / 审批 / 上传 / 下载 / 发送 / 提交 / 完成 / 删除），
 *   绝不记录用户输入的文字、搜索关键词、聊天正文、文件内容或客户信息。
 * · 时间以服务端为准：请求里不发送客户端时间，落库分钟位由服务端时钟决定。
 *
 * ============================== 对外接口 ==============================
 *   startUsageTracking({ api, user })  —— 登录后启动；内部自己处理监听与清理
 *   stopUsageTracking(reason)          —— 退出登录 / 切换账号时立即结束并上报
 *   trackAction(type, count = 1)       —— 业务动作上报入口，页面按需调用
 *   trackVisit(path)                   —— 模块打开次数（路由切换时自动调用）
 *   getUsageSessionId()                —— 当前会话 ID（调试 / 测试用）
 */

import { useEffect } from "react";

/** 心跳间隔（毫秒）：与后端 HEARTBEAT_SEC 一致。 */
const HEARTBEAT_MS = 60 * 1000;
/** 空闲阈值（毫秒）：5 分钟无交互后不再累计有效时长。 */
const IDLE_MS = 5 * 60 * 1000;
/** 多标签选主租约（毫秒）：主标签定时续租，失联后其他标签接管。 */
const LEADER_LEASE_MS = 70 * 1000;
/** 业务动作允许的类型（与后端 ACTION_TYPES 一致，防止前端写错键被静默丢弃）。 */
export const TRACKED_ACTIONS = ["search", "create", "edit", "approve", "upload", "download", "send", "submit", "complete", "delete"];

const SESSION_KEY = "ali_usage_sid";
const TAB_KEY = "ali_usage_tab";
const LEADER_KEY = "ali_usage_leader";

/* ------------------------------------------------------------------ */
/* 模块 ID：把前端路由归一化成「业务模块」，与后端 MODULE_CATALOG 对齐。       */
/* ------------------------------------------------------------------ */

/**
 * 根路径归到哪个模块：`/overview`（总览看板）。
 * App.js 里 `/`（Landing）与 `/overview`（Dashboard）渲染的是同一个页面，
 * 不归一的话总览看板的访问会被拆成两条互不相干的记录（`/` 和 `/overview`）。
 */
const ROOT_MODULE = "/overview";

/**
 * 路由 → 模块 key 的静态映射（最长前缀优先，保证 /admin/users 不会落成 /admin）。
 * 这里用「路由」而不是中文名，是因为服务端统计按路由归集、页面展示时才翻译成业务名称；
 * 新页面只要在 MODULE_CATALOG 里登记过，就能被自动识别，无需改这张表。
 */
const MODULE_PREFIXES = [
  "/admin/usage", "/admin/users", "/admin/roles", "/admin/branches", "/admin/menus",
  "/admin/feedback", "/admin/owner-sms", "/admin/ai", "/admin/google-maps", "/admin/google-calendar", "/admin/sms-consent", "/admin/migration",
  "/admin/i18n", "/admin/trade-pricing", "/admin/nav-places", "/admin/pre-publish",
  "/design/projects", "/design/presign", "/design/dashboard", "/design/tasks", "/design/todo",
  "/design/approvals", "/design/sign-dates", "/design/team", "/design/automation", "/design/ai",
  "/design/settings", "/design/reports", "/design/changes", "/design/struct-orders",
  "/crm/leads", "/crm/dashboard", "/crm/signed", "/crm/payment-calendar", "/crm/contracts",
  "/crm/mm-review", "/crm/price-approvals", "/crm/pool", "/crm/lost-analytics", "/crm/overdue",
  "/crm/assistant-log", "/crm/eval-invites", "/crm/automations",
  "/finance/projects", "/finance/funds", "/finance/budget-defaults", "/finance/imports",
  "/finance/my-card", "/finance-center", "/finance/batch-collect",
  "/hrms/employees", "/hrms/org", "/hrms/planning", "/hrms/recruiting", "/hrms/schedule",
  "/hrms/attendance", "/hrms/compensation", "/hrms/benefits", "/hrms/cn-payroll", "/hrms/us-payroll",
  "/hrms/onboarding", "/hrms/performance", "/hrms/relations", "/hrms/safety", "/hrms/training",
  "/hrms/talent", "/hrms/me", "/hrms/my-schedule", "/hrms/transitions",
  "/tm/homeowners", "/tm/realtors", "/tm/workbench", "/tm/today", "/tm/tasks", "/tm/my-leads",
  "/tm/b2b", "/tm/funnel", "/tm/qa", "/tm/imports", "/tm/invalid", "/tm/settings", "/tm/audit",
  "/warehouses/scan", "/warehouses/bin", "/warehouses", "/warehouse-dashboard", "/warehouse-audit",
  "/projects/map", "/project", "/rectify", "/satisfaction", "/online-pm", "/site-inspections",
  "/sub-checkin-board", "/change-orders", "/subcontractor-companies", "/sign-followup",
  "/archive", "/board", "/overview", "/ceo/administration", "/ceo", "/reminders", "/import",
  "/ai", "/collab", "/chat", "/my-mentions", "/calls", "/agents", "/call-analytics",
  "/outbound-list", "/golden-scripts", "/payments", "/recycle-bin", "/portal", "/inventory",
  "/audit-log", "/quickbooks", "/nudge-stats", "/my-today", "/done-report", "/todos",
];

/**
 * 归一化模块：去掉 query / hash / 动态 id，再按最长前缀匹配。
 *
 * 根路径 `/` 归一到 `/overview`（总览看板）：
 *   App.js:367 `<Route path="/" element={<Landing />} />`，Landing 在没有「账号自定义首页」
 *   时渲染的是 Dashboard，而 Dashboard 的正规路由就是 App.js:368 的 `/overview`。
 *   两者是同一个页面的两个地址。
 *
 *   为什么必须归一（2026-10-20 实测缺陷）：早期实现对根路径退化返回 `/` 本身，把这些
 *   访问当成模块 `/` 上报，结果时间线里堆满「打开 /」（开发库 220 条 view 事件有 213 条
 *   是 `/`），需求要的「访问了哪些页面」完全看不出来。而归一而不是丢弃，是因为
 *   admin / CEO 这类账号的登录首页正是 `/`，直接丢会让他们停在总览看板的停留时长归零。
 *
 * @returns {string} 归一化模块路由（如 /crm/leads）；确实无法归到任何模块时返回 ""
 */
export function moduleForPath(pathname) {
  let path = String(pathname || "/").split("?")[0].split("#")[0];
  if (!path.startsWith("/")) path = "/" + path;
  // 动态段归一：/project/xxx → /project，/agents/xxx → /agents，/crm/leads/xxx → /crm/leads
  path = path.replace(/^\/(project|agents|chat\/c|chat\/m|design\/projects|design\/changes)\/[^/]+.*$/, "/$1");
  let best = "";
  for (const prefix of MODULE_PREFIXES) {
    if ((path === prefix || path.startsWith(prefix + "/")) && prefix.length > best.length) best = prefix;
  }
  if (best) return best;
  // 未登记的新路由：退化成一级路径，保证仍能统计到「有使用」，不会写成空模块。
  const first = path.split("/").filter(Boolean)[0];
  if (!first) return ROOT_MODULE;
  return "/" + first;
}

/* ------------------------------------------------------------------ */
/* 会话与主标签选主                                                     */
/* ------------------------------------------------------------------ */

let state = null;

function readSessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
  } catch { /* 隐私模式禁用存储时降级为内存 session */ }
  const sid = randomId();
  try { sessionStorage.setItem(SESSION_KEY, sid); } catch { /* ignore */ }
  return sid;
}

function randomId() {
  const buf = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(buf);
  else for (let i = 0; i < buf.length; i += 1) buf[i] = Math.floor(Math.random() * 256);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * 本标签的身份 ID。
 *
 * 【2026-10-20 修复】原先每次 startUsageTracking 都新生成一个随机 tabId。
 * 但客户端选主租约（LEADER_KEY，70 秒）存在 localStorage 里、跨刷新保留，
 * 于是「刚上报过 → 刷新页面」时，新 tabId 与租约里的旧 tabId 不一致，
 * 且租约尚未过期（70 秒内），isActiveTab() 会把自己判成副标签，
 * **刷新后的页面在最长 70 秒内完全不发心跳**；若刷新时主标签租约被反复续上，
 * 表现就是「一直在用却统计不到时长」。
 *
 * 修复方式：把 tabId 存进 sessionStorage —— 同一标签刷新后沿用同一个 ID
 * （租约仍然匹配，继续上报），新开的标签则天然拿到不同的 ID（保持多标签选主语义）。
 */
function readTabId() {
  try {
    const existing = sessionStorage.getItem(TAB_KEY);
    if (existing) return existing;
  } catch { /* 隐私模式：退回内存随机 ID */ }
  const id = randomId();
  try { sessionStorage.setItem(TAB_KEY, id); } catch { /* ignore */ }
  return id;
}

/** 读取本浏览器的选主记录（同一浏览器不同标签共享 localStorage）。 */
function readLeader() {
  try {
    const raw = localStorage.getItem(LEADER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeLeader(tabId) {
  try {
    localStorage.setItem(LEADER_KEY, JSON.stringify({ tabId, at: Date.now() }));
  } catch { /* ignore */ }
}

/**
 * 本标签是否是「当前有效标签」。
 * 只在多种原因下返回 false：其它标签持有未过期租约。这样既减少了重复请求，
 * 也保证主标签被关掉后其它标签能在 70 秒内接管，不会出现「没人上报」。
 */
function isActiveTab(tabId) {
  const leader = readLeader();
  if (!leader) { writeLeader(tabId); return true; }
  if (leader.tabId === tabId) { writeLeader(tabId); return true; }
  if (Date.now() - (leader.at || 0) > LEADER_LEASE_MS) { writeLeader(tabId); return true; }
  return false;
}

/* ------------------------------------------------------------------ */
/* 设备信息                                                             */
/* ------------------------------------------------------------------ */

function detectDevice() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const type = /iPad|Tablet/i.test(ua) ? "tablet" : /Mobi|Android|iPhone/i.test(ua) ? "mobile" : "desktop";
  const os = /Windows/i.test(ua) ? "Windows" : /Mac OS X|Macintosh/i.test(ua) ? "macOS"
    : /Android/i.test(ua) ? "Android" : /iPhone|iPad|iOS/i.test(ua) ? "iOS" : /Linux/i.test(ua) ? "Linux" : "其他";
  const browser = /Edg\//i.test(ua) ? "Edge" : /OPR\//i.test(ua) ? "Opera" : /Chrome\//i.test(ua) ? "Chrome"
    : /Safari\//i.test(ua) ? "Safari" : /Firefox\//i.test(ua) ? "Firefox" : "其他";
  return { type, os, browser };
}

/* ------------------------------------------------------------------ */
/* 业务动作推断：从真实接口调用反推「用户做了什么」                        */
/* ------------------------------------------------------------------ */

/**
 * 接口路径 → 业务动作类型。
 *
 * 需求：使用深度要能统计「搜索 / 创建 / 编辑 / 审批 / 上传 / 下载 / 发送 / 提交 / 完成」，
 * 但项目里有几百个页面，逐个页面埋点既容易漏、又会把业务代码改得满屏埋点。
 * 折中方案：在统一的 axios 实例上按「HTTP 方法 + 路径关键字」推断动作类型 ——
 * 它只记录**动作类型与次数**，不记录参数、请求体、响应体，因此不涉及任何内容采集。
 *
 * 只统计与业务数据相关的写操作；登录、权限、轮询、使用分析自身接口一律跳过，
 * 避免把「每分钟自动刷新」算成用户动作。
 */
const ACTION_RULES = [
  // 上传：文件 / 附件 / 图纸 / 照片（POST/PUT multipart 全部归到上传）
  { test: (m, u) => /\/(files?|attachments?|documents?|uploads?|photos?|drawings?)(\/|$)/i.test(u) && ["post", "put", "patch"].includes(m), action: "upload" },
  // 下载：显式下载 / 导出 / PDF / Excel
  { test: (m, u) => /(download|export|\.pdf$|\/pdf\/|excel|xlsx)/i.test(u) && ["get", "post"].includes(m), action: "download" },
  // 审批：approve / reject / deny / sign-off
  { test: (m, u) => /(approve|approval|reject|deny|sign[-_]?off|review\/decision)/i.test(u) && ["post", "put", "patch"].includes(m), action: "approve" },
  // 发送：消息 / 邮件 / 短信 / 回复
  { test: (m, u) => /(send|messages?|mails?|sms|reply|notify)/i.test(u) && ["post", "put"].includes(m), action: "send" },
  // 提交：submit / 提交表单 / 打卡
  { test: (m, u) => /(submit|check-?in|clock-?in)/i.test(u) && ["post", "put", "patch"].includes(m), action: "submit" },
  // 完成：完成 / 完结 / 关闭任务
  { test: (m, u) => /(complete|finish|done|close)/i.test(u) && ["post", "put", "patch"].includes(m), action: "complete" },
  // 搜索：带查询参数的读取请求（30 秒窗口去重，见 trackSearch）
  { test: (m, u, cfg) => m === "get" && /(search|lookup|\?(.*&)?(q|kw|keyword)=)/i.test(u + "?" + new URLSearchParams(cfg?.params || {}).toString()), action: "search" },
  // 删除
  { test: (m) => m === "delete", action: "delete" },
  // 编辑（PUT / PATCH 兜底）
  { test: (m) => ["put", "patch"].includes(m), action: "edit" },
  // 创建（POST 兜底）
  { test: (m) => m === "post", action: "create" },
];

/**
 * 使用分析自身 / 认证 / 心跳等接口不参与业务动作统计（否则会污染使用深度）。
 *
 * 【2026-10-20 修复】这里原先只有 usage/auth 等少数几条，导致**后台自动轮询**被
 * 当成用户动作：软电话每 20 秒 POST 一次 /telephony/presence，会被 POST 兜底规则
 * 判成「创建」；而 trackAction() 又会刷新 lastInteraction（最后交互时间）。
 * 两者叠加的后果是——人已经离开电脑，心跳仍然 active=true，5 分钟空闲阈值
 * 永远不生效，有效时长会一直累计到下班（实测静默 8 分钟后 active 仍为 true）。
 *
 * 因此把所有「客户端定时自动发起、不代表用户操作」的接口登记为忽略：
 *   · presence / unread / pull / poll / heartbeat / ping：存活性与未读数轮询；
 *   · voice-token / sync：通话与同步令牌续期；
 *   · notifications / reminders 的轮询式读取由页面自身决定，这里只挡明确的后台信标。
 * 判断口径是「无人操作时也会周期性发出」——这类请求不能算动作，也不能续命空闲计时。
 */
const IGNORED_PATHS = [
  "/usage/", "/auth/", "/login", "/heartbeat", "/healthz", "/audit-logs",
  "/telephony/presence", "/telephony/voice-token", "/telephony/sms-inbox",
  "/unread-summary", "/chat/unread", "/recycle/status", "/reminders/unread",
  "/presence", "/poll", "/ping", "/keepalive",
  // 语言 / 主题偏好是应用初始化时自动写回的（不是用户主动改设置），
  // 否则每次登录都会凭空多出两条「编辑」动作，把人人都变成重度用户。
  "/users/me/lang", "/users/me/theme", "/users/me/prefs",
];

/** 搜索类请求的去重窗口：列表页每输入一个字就请求一次，不能算成多次搜索。 */
const SEARCH_DEDUPE_MS = 30 * 1000;
const lastSearchAt = {};

function actionForRequest(config) {
  const method = String(config?.method || "get").toLowerCase();
  const url = String(config?.url || "");
  if (!url || IGNORED_PATHS.some((p) => url.includes(p))) return "";
  for (const rule of ACTION_RULES) {
    if (rule.test(method, url, config)) return rule.action;
  }
  return "";
}

/* ------------------------------------------------------------------ */
/* 采集主体                                                             */
/* ------------------------------------------------------------------ */

/**
 * 启动采集。
 *
 * @param {object} opts
 * @param {object} opts.api     axios 实例（带鉴权头）
 * @param {object} opts.user    当前登录用户（只用 username 判断是否切换账号）
 * @returns {function} 停止函数
 */
export function startUsageTracking({ api, user }) {
  if (!api || !user?.username) return () => {};
  stopUsageTracking("restart");

  const tabId = readTabId();
  const sid = readSessionId();
  const username = user.username;
  const device = detectDevice();

  state = {
    api, username, sid, tabId, device,
    visible: typeof document !== "undefined" ? document.visibilityState !== "hidden" : true,
    // 最后一次真实交互时间；只在「有交互」时推进，切后台不推进，所以回前台后需要重新操作才继续累计。
    lastInteraction: Date.now(),
    lastPath: typeof window !== "undefined" ? window.location.pathname : "/",
    pendingActions: {},
    pendingVisits: {},
    // 逐条操作明细：与 pendingActions 是「同一批事实的两种形态」——
    // actions 给统计用（计数），pendingEvents 给明细时间线用（逐条）。
    // 每条只放 kind / module / action / at 四个字段，绝不放输入内容。
    pendingEvents: [],
    inFlight: false,
    timer: null,
    destroyed: false,
    // 拦截器句柄先占位：axios 拦截器在本函数下方才注册，若此处直接引用
    // reqInterceptor / resInterceptor 这两个 const，会命中暂时性死区（TDZ）报
    // "Cannot access before initialization"，导致登录后整个应用白屏。
    reqInterceptor: null,
    resInterceptor: null,
  };

  // 打开页面即记一次当前模块的访问（首屏也算一次使用）。
  // moduleForPath 把 `/` 归一成 `/overview`，所以 admin / CEO 直接落在总览看板时也记得住。
  const initialModule = moduleForPath(state.lastPath);
  state.pendingVisits[initialModule] = 1;
  // 同时记一条时间线明细：老板点开某人时，能看到「几点打开了哪个页面」。
  pushEvent("view", initialModule);

  const markInteraction = () => {
    if (!state || state.destroyed) return;
    state.lastInteraction = Date.now();
  };

  /**
   * 压入一条操作明细（时间线用）。
   *
   * ⚠️ 必须定义在第一次调用之前。`const` 声明的函数有暂时性死区（TDZ）：
   * 一旦在声明前调用就会抛 "Cannot access 'pushEvent' before initialization"，
   * 而本模块在登录后立即执行，结果是**整个应用白屏**（2026-10-20 实测踩过一次）。
   * 所以这里用 function 声明（提升）而不是 const 箭头函数，从机制上消除该风险。
   *
   * 隐私红线：只记「类型 + 模块路由 + 时间」，**不记任何输入内容**——
   * 不记搜索关键词、不记聊天正文、不记文件名、不记客户信息。
   * 模块传的是归一化路由（/crm/leads），服务端再翻译成业务名称给页面。
   *
   * @param {"view"|"action"} kind 事件类型
   * @param {string} module 归一化模块路由
   * @param {string} action 业务动作类型（kind=action 时必填）
   */
  function pushEvent(kind, module, action = "") {
    if (!state || state.destroyed) return;
    // 页面访问 / 业务操作必须说清「在哪个模块」。module 传空时退回当前路径的归一化结果；
    // 归一化后仍为空只可能是路径本身异常（正常路径最差也会归到一级路径），此时丢弃。
    // login / logout 不属于任何页面，允许没有模块（与服务端 _clean_event 的白名单一致）。
    const resolved = module || moduleForPath(state.lastPath);
    if (!resolved && kind !== "login" && kind !== "logout") return;
    // 上限保护：一次心跳最多带 50 条（与后端 MAX_EVENTS_PER_BEAT 一致），
    // 超出部分丢弃最旧的——明细是「看趋势」用的，不需要无限缓冲。
    if (state.pendingEvents.length >= 50) state.pendingEvents.shift();
    state.pendingEvents.push({
      kind,
      module: resolved,
      action,
      at: new Date().toISOString(),
    });
  }
  state.pushEvent = pushEvent;

  const onVisible = () => {
    if (!state || state.destroyed) return;
    state.visible = document.visibilityState !== "hidden";
    if (state.visible) {
      // 回到前台时重置交互时间：刚回来还没有操作，必须先有交互才开始累计有效时长。
      state.lastInteraction = Date.now();
      // 立即上报「恢复了前台」，不等下一个 60 秒，保证恢复后的分钟不被漏记。
      flush("visible");
    } else {
      flush("hidden");
    }
  };

  const onPageHide = () => {
    if (!state || state.destroyed) return;
    state.visible = false;
    flush("pagehide", true);
  };

  const onRoute = () => {
    if (!state || state.destroyed) return;
    const path = window.location.pathname;
    if (path === state.lastPath) return;
    state.lastPath = path;
    const module = moduleForPath(path);
    if (!module) return;
    state.pendingVisits[module] = (state.pendingVisits[module] || 0) + 1;
    // 路由切换同时记一条时间线明细：这是「访问了什么页面」最准确的来源，
    // 比按心跳周期采样更贴近真实浏览顺序。
    pushEvent("view", module);
  };

  // 业务动作采集：挂在统一 axios 实例上，只对「成功返回」的请求计数，
  // 失败 / 取消的请求不算用户动作。注销时机在 stopUsageTracking 里。
  const requestMarks = new WeakMap();
  const reqInterceptor = api.interceptors.request.use((config) => {
    const action = actionForRequest(config);
    if (action) requestMarks.set(config, action);
    return config;
  });
  const resInterceptor = api.interceptors.response.use((response) => {
    const action = requestMarks.get(response?.config);
    if (action) {
      if (action === "search") {
        // 同一路径 30 秒内只记 1 次：列表搜索通常边输边查，按请求数统计会严重高估。
        const key = String(response?.config?.url || "").split("?")[0];
        const now = Date.now();
        if (!lastSearchAt[key] || now - lastSearchAt[key] > SEARCH_DEDUPE_MS) {
          lastSearchAt[key] = now;
          trackAction("search");
        }
      } else {
        trackAction(action);
      }
    }
    return response;
  });
  // 注册完成后回填句柄，供 stopUsageTracking 注销。
  state.reqInterceptor = reqInterceptor;
  state.resInterceptor = resInterceptor;

  const listeners = [
    ["visibilitychange", onVisible, document],
    ["pagehide", onPageHide, window],
    ["beforeunload", onPageHide, window],
    ["mousemove", markInteraction, window],
    ["mousedown", markInteraction, window],
    ["keydown", markInteraction, window],
    ["wheel", markInteraction, window],
    ["touchstart", markInteraction, window],
    ["scroll", markInteraction, window],
    ["popstate", onRoute, window],
  ];
  listeners.forEach(([event, handler, target]) => target && target.addEventListener(event, handler, { passive: true }));

  // 路由变化（React Router 不会触发 popstate）：用轮询感知 location.pathname 变化，
  // 比在每个页面手动调用更可靠，代价是每秒一次字符串比较。
  const routeTimer = setInterval(onRoute, 1000);
  state.routeTimer = routeTimer;
  state.listeners = listeners;

  /**
   * 组装并发送一次心跳。
   *
   * @param {string} reason 触发原因（timer / visible / hidden / pagehide / action / manual）
   * @param {boolean} urgent 紧急上报：页面即将关闭时用 keepalive 尽力送达
   */
  async function flush(reason = "timer", urgent = false) {
    if (!state || state.destroyed || state.inFlight) return;
    // 非主标签不上报：避免同一浏览器多标签重复请求（服务端本身已去重，这里只省流量）。
    if (!isActiveTab(tabId)) return;
    // 【2026-10-23 修复】用局部变量持有本次心跳对应的 state。
    // 原实现在下方 finally 里直接写 `state.inFlight = false`，而心跳是 async 的：
    // 请求在途期间用户若退出登录 / 切换账号，stopUsageTracking 会把模块级 state 置为 null，
    // 于是 await 回来后 `state.inFlight = false` 抛
    // 「Cannot set properties of null (setting 'inFlight')」——线上实测每次退出/换账号必现。
    // 改为操作局部引用后，即使模块级 state 已失效，也只会写到一个已经没人用的旧对象上，不再抛错。
    const s = state;
    const idle = Date.now() - s.lastInteraction > IDLE_MS;
    const effective = s.visible && !idle;
    const actions = s.pendingActions;
    const visits = s.pendingVisits;
    const events = s.pendingEvents;
    // 只有「页面访问」而没有业务动作时也要上报，否则时间线里看不到浏览轨迹。
    if (!effective && !Object.keys(actions).length && !Object.keys(visits).length && !events.length) {
      // 后台 / 空闲且没有待上报事件：仅续租，不打扰服务端。
      return;
    }
    s.pendingActions = {};
    s.pendingVisits = {};
    s.pendingEvents = [];
    s.inFlight = true;
    const payload = {
      sid,
      eventId: randomId(),
      visible: s.visible,
      active: !idle,
      module: moduleForPath(s.lastPath),
      actions,
      visits,
      events,
      device,
      reason,
    };
    try {
      await api.post("/usage/heartbeat", payload, urgent ? { keepalive: true } : undefined);
    } catch {
      // 失败（断网 / 5xx）：把增量还回去，等下一次心跳重试；eventId 不同也不会双记，
      // 因为这次请求没有成功落库（若实际已落库，服务端 eventId 去重会兜住极端重试）。
      if (!s.destroyed || urgent) {
        Object.keys(actions).forEach((k) => { s.pendingActions[k] = (s.pendingActions[k] || 0) + actions[k]; });
        Object.keys(visits).forEach((k) => { s.pendingVisits[k] = (s.pendingVisits[k] || 0) + visits[k]; });
        // 明细还回去时放回队首，保持时间顺序；仍受 50 条上限约束。
        s.pendingEvents = events.concat(s.pendingEvents).slice(-50);
      }
    } finally {
      s.inFlight = false;
    }
  }

  state.flush = flush;
  // 立即先报一次：登录后马上就有「今天活跃 / 在线」的数据，不用等满 1 分钟。
  flush("start");
  state.timer = setInterval(() => flush("timer"), HEARTBEAT_MS);
  // 业务动作发生时尽快上报，最多每秒一次，避免高频点击打爆接口。
  state.actionTimer = setInterval(() => {
    if (state && Object.keys(state.pendingActions).length) flush("action");
  }, 5 * 1000);

  return () => stopUsageTracking("manual");
}

/**
 * 停止采集并结束会话。
 *
 * 退出登录时必须调用：把「已结束」立即写回服务端，避免会话时长一直挂到超时，
 * 也让老板在「当前在线」里看到的是真实在线人数。
 */
export function stopUsageTracking(reason = "logout") {
  if (!state) return;
  const current = state;
  state = null;
  current.destroyed = true;
  clearInterval(current.timer);
  clearInterval(current.actionTimer);
  clearInterval(current.routeTimer);
  (current.listeners || []).forEach(([event, handler, target]) => target && target.removeEventListener(event, handler));
  // 注销业务动作拦截器：退出登录后不再采集（换账号时会重新注册一套）。
  try {
    if (current.reqInterceptor != null) current.api.interceptors.request.eject(current.reqInterceptor);
    if (current.resInterceptor != null) current.api.interceptors.response.eject(current.resInterceptor);
  } catch { /* ignore */ }
  // 先尽力发最后一个心跳（把剩余增量送出去），再结束会话。
  const finish = async () => {
    try {
      const restEvents = (current.pendingEvents || []).slice(-50);
      // 退出登录也留一条时间线：这是「会话结束」最明确的信号，
      // 让老板能看出「几点退出的」，而不是会话莫名其妙停在某个时刻。
      if (reason && reason !== "restart" && reason !== "manual") {
        restEvents.push({ kind: "logout", module: "", action: "", at: new Date().toISOString() });
      }
      if (current.visible || Object.keys(current.pendingActions || {}).length || restEvents.length) {
        await current.api.post("/usage/heartbeat", {
          sid: current.sid, eventId: randomId(), visible: false, active: false,
          module: moduleForPath(current.lastPath), actions: current.pendingActions || {},
          visits: current.pendingVisits || {}, events: restEvents,
          device: current.device, reason: "stop",
        }, { keepalive: true });
      }
    } catch { /* 忽略：会话结束是尽力而为 */ }
    try {
      await current.api.post("/usage/session/end", { sid: current.sid, reason }, { keepalive: true });
    } catch { /* 忽略：服务端超时也会自动结束 */ }
  };
  finish();
}

/** 记录一次业务动作（只记类型与次数，不记内容）。 */
export function trackAction(type, count = 1) {
  if (!state || state.destroyed) return;
  if (!TRACKED_ACTIONS.includes(type)) return;
  const n = Math.max(0, Math.min(50, Math.floor(Number(count) || 0)));
  if (!n) return;
  state.pendingActions[type] = (state.pendingActions[type] || 0) + n;
  // 同时压入逐条明细：老板点开某人后能看到「几点在哪个页面做了什么操作」。
  // 一次上报 n 次动作就压 n 条（n 已被限制在 50 以内），保证时间线条数与计数不脱节。
  if (typeof state.pushEvent === "function") {
    for (let i = 0; i < n; i += 1) state.pushEvent("action", moduleForPath(state.lastPath), type);
  }
  // 交互事件：业务动作本身也证明人在电脑前，推迟空闲判定。
  state.lastInteraction = Date.now();
}

/** 记录一次模块打开（路由切换时自动触发；页面也可手动调用以统计站内弹层）。 */
export function trackVisit(path) {
  if (!state || state.destroyed) return;
  const module = moduleForPath(path || (typeof window !== "undefined" ? window.location.pathname : "/"));
  if (!module) return;
  state.pendingVisits[module] = (state.pendingVisits[module] || 0) + 1;
  if (typeof state.pushEvent === "function") state.pushEvent("view", module);
}

export function getUsageSessionId() {
  return state?.sid || null;
}

/**
 * React Hook：登录后在 Layout 内挂一次即可（登录页不采集）。
 * 依赖 username：切换账号（含代操作）时自动停止旧会话、开新会话。
 */
export function useUsageTracking(api, user) {
  const username = user?.username || "";
  useEffect(() => {
    if (!username) return undefined;
    return startUsageTracking({ api, user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, username]);
}
